import type { AppLinkProps } from '@/hooks/usePathHelper'
import type { ProductRole } from '@/lib/product-role'
import type { useSoftDeleteProductMutation } from '@/lib/services/products/products.query'
import type { Product } from '@/lib/services/products/products.types'
import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { EllipsisVertical, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useAbility } from '@/lib/casl'
import { getProductRoleLabel } from '@/lib/services/products/products.form'
import { getErrorMessage } from '@/lib/utils'
import { RecordHistoryMenuItem } from '@/components/audit-log/RecordHistoryMenu'
import { AdminHeaderIconButton } from '@/components/patterns/admin-page'
import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface ProductMoreActionsMenuProps {
  deleteProductMutation: Pick<
    ReturnType<typeof useSoftDeleteProductMutation>,
    'mutateAsync' | 'isPending' | 'cleanupDeletedProduct'
  >
  historyLink?: AppLinkProps | null
  isAddOnProduct?: boolean
  isReadOnly?: boolean
  product: Pick<Product, 'id'>
  productRole?: ProductRole
}

export function ProductMoreActionsMenu({
  deleteProductMutation,
  historyLink,
  isAddOnProduct = false,
  isReadOnly,
  product,
  productRole = isAddOnProduct ? 'standalone_add_on' : 'product',
}: ProductMoreActionsMenuProps) {
  const ability = useAbility()
  const path = usePathHelper()
  const navigate = useNavigate()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const canDeleteProduct = !isReadOnly && ability.can('delete', 'products')
  const canViewHistory = Boolean(historyLink && ability.can('view', 'audit-logs'))

  const deleteProduct = async () => {
    try {
      await deleteProductMutation.mutateAsync(product.id)
      toast.success(`${getProductRoleLabel(productRole)} deleted successfully`)
      await navigate({
        ...path.products.index().link,
        ignoreBlocker: true,
        replace: true,
      })
      void Promise.resolve()
        .then(() => deleteProductMutation.cleanupDeletedProduct?.(product.id))
        .catch(() => undefined)
    } catch (error) {
      toast.error(`Failed to delete ${getProductRoleLabel(productRole, { lowercase: true })}`, {
        description: getErrorMessage(error),
      })
    }
  }

  if (!product?.id || (!canDeleteProduct && !canViewHistory)) {
    return null
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <AdminHeaderIconButton icon={EllipsisVertical} label="More product actions" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" size="sm">
          {canViewHistory ? <RecordHistoryMenuItem link={historyLink} /> : null}
          {canDeleteProduct ? (
            <>
              {canViewHistory ? <DropdownMenuSeparator /> : null}
              <DropdownMenuItem variant="destructive" onSelect={() => setIsDeleteDialogOpen(true)}>
                <Trash2 className="size-4" />
                Delete {getProductRoleLabel(productRole, { lowercase: true })}
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      {canDeleteProduct ? (
        <ConfirmationDialog
          trigger={null}
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          variant="destructive"
          title={`Delete ${getProductRoleLabel(productRole)}`}
          description={`Are you sure you want to delete this ${getProductRoleLabel(productRole, { lowercase: true })}?`}
          confirmText="Delete"
          pendingText="Deleting..."
          isPending={deleteProductMutation.isPending}
          onConfirm={deleteProduct}
        />
      ) : null}
    </>
  )
}
